import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { DOCUMENT_MIME_TYPES } from 'src/meetings/detail/tabs/meeting-note/types';
import { useMeetingAgenda } from 'src/suitability-report/hooks/useMeetingAgendas';

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * An agenda a meeting note can be structured on. An uploaded agenda is a data upload file and is
 * sent as `agendaFileId`; a generated agenda is a meeting agenda and is sent as `meetingAgendaId`.
 */
export type AgendaOption = {
  kind: 'uploaded' | 'generated';
  id: string;
  label: string;
  createdAt: string;
  isDocx: boolean;
};

type Props = {
  clientId?: string;
};

export const useAgendaOptions = ({ clientId }: Props) => {
  const { files, isLoading: isLoadingFiles } = useFileUploads({ clientId });
  const { meetingAgendas, isLoading: isLoadingAgendas } = useMeetingAgenda({
    clientId,
    includeFamily: true,
  });

  const generatedOptions: AgendaOption[] = meetingAgendas
    .filter(agenda => !!agenda.file && agenda.backgroundProcess?.status !== 'FAILED')
    .map(agenda => {
      const label = agenda.fileName ?? agenda.file?.fileName ?? 'Meeting agenda';
      return {
        kind: 'generated',
        id: agenda.id,
        label,
        createdAt: agenda.createdAt,
        isDocx: agenda.file?.fileType === DOCX_MIME_TYPE || isDocxName(label),
      };
    });

  const uploadedOptions: AgendaOption[] = files
    .filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType))
    .map(file => ({
      kind: 'uploaded',
      id: file.id,
      label: file.file.fileName,
      createdAt: file.file.createdAt,
      isDocx: isUploadedDocx(file),
    }));

  return {
    agendaOptions: [...generatedOptions, ...uploadedOptions],
    isLoading: isLoadingFiles || isLoadingAgendas,
  };
};

function isUploadedDocx(file: UploadedFile): boolean {
  return file.file.fileType === DOCX_MIME_TYPE || isDocxName(file.file.fileName);
}

function isDocxName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.docx');
}
